import React from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, NavLink } from 'react-router-dom';
import '../styles/admin.css';

const getTimestamp = (value) => {
  if (!value) {
    return 0;
  }
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const sortByDescendingDate = (items, selector) => {
  if (!Array.isArray(items)) {
    return [];
  }
  const pick = typeof selector === 'function' ? selector : (item) => item?.last_updated;
  return [...items].sort((a, b) => getTimestamp(pick(b)) - getTimestamp(pick(a)));
};

const ADMIN_TOKEN_KEY = 'lown:admin:token';

const buildAdminUrl = (path) => {
  const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  return new URL(path, base);
};

function useAdminToken() {
  const [token, setTokenState] = React.useState(() => {
    if (typeof window === 'undefined') {
      return '';
    }
    const stored = window.localStorage.getItem(ADMIN_TOKEN_KEY);
    return typeof stored === 'string' ? stored : '';
  });

  const setToken = React.useCallback((nextToken) => {
    setTokenState(nextToken);
    if (typeof window === 'undefined') {
      return;
    }
    if (nextToken) {
      window.localStorage.setItem(ADMIN_TOKEN_KEY, nextToken);
    } else {
      window.localStorage.removeItem(ADMIN_TOKEN_KEY);
    }
  }, []);

  const clearToken = React.useCallback(() => {
    setToken('');
  }, [setToken]);

  return React.useMemo(
    () => ({ token, setToken, clearToken }),
    [token, setToken, clearToken]
  );
}

function AdminLogin({ onAuthenticated }) {
  const [tokenInput, setTokenInput] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!tokenInput.trim()) {
      setError('Enter the admin access token.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);

    try {
      const url = buildAdminUrl('/api/admin/support');
      url.searchParams.set('limit', '1');

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${tokenInput.trim()}`,
        },
        signal: controller.signal,
      });
      if (response.status === 401) {
        setError('Access denied. Double-check the admin token.');
        return;
      }

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body?.message || 'Unable to verify token. Try again later.');
      }

      onAuthenticated(tokenInput.trim());

      const params = new URLSearchParams(location.search);
      const redirectPath = params.get('redirect') || '/admin';
      navigate(redirectPath, { replace: true });
    } catch (err) {
      setError(err?.message || 'Unexpected error during sign in.');
    } finally {
      clearTimeout(timeout);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-portal admin-portal--login">
      <div className="admin-card">
        <h1>Admin Portal</h1>
        <p className="admin-card__subtitle">Sign in with your secure admin token.</p>
        <form onSubmit={handleSubmit} className="admin-form">
          <label htmlFor="admin-token">Admin Token</label>
          <input
            id="admin-token"
            type="password"
            value={tokenInput}
            onChange={(event) => setTokenInput(event.target.value)}
            placeholder="Paste admin token"
            autoComplete="off"
            spellCheck="false"
            disabled={isSubmitting}
            required
          />
          {error ? <p className="admin-form__error">{error}</p> : null}
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Verifying…' : 'Sign In'}
          </button>
        </form>
        <p className="admin-card__help">Need help? Contact the platform owner.</p>
      </div>
    </div>
  );
}

function AdminPreferencesViewer({ token, onUnauthorized }) {
  const [entries, setEntries] = React.useState([]);
  const [limit, setLimit] = React.useState(25);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [pagination, setPagination] = React.useState({ nextCursor: null });
  const [isSearching, setIsSearching] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');

  const fetchPreferences = React.useCallback(
    async ({ cursor = null, append = false, userId = null } = {}) => {
      setIsLoading(true);
      setError('');

      try {
        const url = buildAdminUrl('/api/admin/notifications/preferences');
        url.searchParams.set('limit', String(limit));
        if (cursor) {
          url.searchParams.set('cursor', cursor);
        }
        if (userId) {
          url.searchParams.set('user_id', userId);
        }

        const response = await fetch(url.toString(), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 401) {
          onUnauthorized();
          return;
        }

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body?.message || 'Failed to load notification preferences.');
        }

        const data = await response.json();

        if (userId && data.result) {
          setEntries(sortByDescendingDate([data.result], (item) => item.last_updated));
          setPagination({ nextCursor: null, userSpecific: true });
          return;
        }

        const results = Array.isArray(data.results) ? data.results : [];
        const sortedResults = sortByDescendingDate(results, (item) => item.last_updated);
        setEntries((prev) =>
          append
            ? sortByDescendingDate(prev.concat(sortedResults), (item) => item.last_updated)
            : sortedResults
        );
        setPagination({
          nextCursor: data.pagination?.nextCursor || null,
          limit: data.pagination?.limit || limit,
        });
      } catch (err) {
        setError(err?.message || 'Unexpected error loading preferences.');
      } finally {
        setIsLoading(false);
      }
    },
    [limit, onUnauthorized, token]
  );

  React.useEffect(() => {
    if (!isSearching) {
      fetchPreferences({ cursor: null, append: false });
    }
  }, [fetchPreferences, limit, isSearching]);

  const handleSearch = async (event) => {
    event.preventDefault();
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      setIsSearching(false);
      fetchPreferences({ cursor: null, append: false });
      return;
    }
    setIsSearching(true);
    await fetchPreferences({ userId: trimmed });
  };

  const clearSearch = () => {
    setSearchTerm('');
    setIsSearching(false);
    fetchPreferences({ cursor: null, append: false });
  };

  const loadMore = async () => {
    if (!pagination.nextCursor) {
      return;
    }
    await fetchPreferences({ cursor: pagination.nextCursor, append: true });
  };

  return (
    <div className="admin-panel">
      <header className="admin-panel__header">
        <div>
          <h2>Notification Preferences</h2>
          <p>Review preference selections across users.</p>
        </div>
        <form className="admin-panel__search" onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Lookup by user ID"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            disabled={isLoading}
          />
          <button type="submit" disabled={isLoading}>
            Search
          </button>
          {isSearching ? (
            <button type="button" onClick={clearSearch} disabled={isLoading}>
              Clear
            </button>
          ) : null}
        </form>
        <div className="admin-panel__filters">
          <label>
            Page Size
            <select
              value={limit}
              onChange={(event) => {
                setPagination({ nextCursor: null });
                setEntries([]);
                setLimit(Number(event.target.value));
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </label>
        </div>
      </header>

      {error ? <div className="admin-alert admin-alert--error">{error}</div> : null}

      {isLoading && entries.length === 0 ? (
        <div className="admin-loading">Loading notification preferences…</div>
      ) : entries.length === 0 ? (
        <div className="admin-empty">No notification preferences found.</div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table admin-table--wide">
            <thead>
              <tr>
                <th>User ID</th>
                <th>Frequency</th>
                <th>Email</th>
                <th>Push</th>
                <th>SMS</th>
                <th>Marketing</th>
                <th>Feature Updates</th>
                <th>Goal Progress</th>
                <th>New Content</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.user_id || entry.preferences?.email}>
                  <td>{entry.user_id || '—'}</td>
                  <td>{entry.preferences?.frequency || '—'}</td>
                  <td>{entry.preferences?.email ? 'Yes' : 'No'}</td>
                  <td>{entry.preferences?.push ? 'Yes' : 'No'}</td>
                  <td>{entry.preferences?.sms ? 'Yes' : 'No'}</td>
                  <td>{entry.preferences?.marketing ? 'Yes' : 'No'}</td>
                  <td>{entry.preferences?.feature_updates ? 'Yes' : 'No'}</td>
                  <td>{entry.preferences?.goal_progress ? 'Yes' : 'No'}</td>
                  <td>{entry.preferences?.new_content ? 'Yes' : 'No'}</td>
                  <td>{entry.last_updated ? new Date(entry.last_updated).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <footer className="admin-pagination">
        <div className="admin-pagination__info">
          {isSearching ? 'Search results' : `${entries.length} record${entries.length === 1 ? '' : 's'} loaded`}
        </div>
        <div className="admin-pagination__actions">
          <button type="button" onClick={() => fetchPreferences({ cursor: null })} disabled={isLoading}>
            Refresh
          </button>
          <button
            type="button"
            onClick={loadMore}
            disabled={isLoading || !pagination.nextCursor || isSearching}
          >
            Load More
          </button>
        </div>
      </footer>
    </div>
  );
}

function AdminSupportRecords({ token, onUnauthorized }) {
  const [records, setRecords] = React.useState([]);
  const [limit, setLimit] = React.useState(25);
  const [offset, setOffset] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [searchMode, setSearchMode] = React.useState('email');
  const [searchValue, setSearchValue] = React.useState('');
  const [pagination, setPagination] = React.useState({ totalCount: 0, nextOffset: null });

  const fetchRecords = React.useCallback(
    async ({ nextOffset = 0, userQuery = null, queryType = null } = {}) => {
      setIsLoading(true);
      setError('');

      try {
        const url = buildAdminUrl('/api/admin/support/records');
        if (userQuery && queryType === 'id') {
          url.searchParams.set('id', userQuery);
        } else if (userQuery && queryType === 'email') {
          url.searchParams.set('email', userQuery);
        } else {
          url.searchParams.set('limit', String(limit));
          url.searchParams.set('offset', String(nextOffset));
        }

        const response = await fetch(url.toString(), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 401) {
          onUnauthorized();
          return;
        }

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body?.message || 'Failed to load support requests.');
        }

        const data = await response.json();

        if (userQuery) {
          const results = data.result ? [data.result] : Array.isArray(data.results) ? data.results : [];
          setRecords(sortByDescendingDate(results, (item) => item.created_at));
          setPagination({ totalCount: results.length, nextOffset: null });
          return;
        }

        const results = Array.isArray(data.results) ? data.results : [];
        const sortedResults = sortByDescendingDate(results, (item) => item.created_at);
        setRecords(sortedResults);
        setPagination({
          totalCount: data.pagination?.totalCount || results.length,
          nextOffset: data.pagination?.nextOffset,
          offset: data.pagination?.offset || 0,
        });
        setOffset(nextOffset);
      } catch (err) {
        setError(err?.message || 'Unexpected error loading support requests.');
      } finally {
        setIsLoading(false);
      }
    },
    [limit, onUnauthorized, token]
  );

  React.useEffect(() => {
    if (!searchValue) {
      fetchRecords({ nextOffset: 0 });
    }
  }, [fetchRecords, limit, searchValue]);

  const handleSearch = async (event) => {
    event.preventDefault();
    const trimmed = searchValue.trim();
    if (!trimmed) {
      setSearchValue('');
      fetchRecords({ nextOffset: 0 });
      return;
    }
    await fetchRecords({ userQuery: trimmed, queryType: searchMode });
  };

  const clearSearch = () => {
    setSearchValue('');
    fetchRecords({ nextOffset: 0 });
  };

  const loadNext = async () => {
    if (pagination.nextOffset == null) {
      return;
    }
    await fetchRecords({ nextOffset: pagination.nextOffset });
  };

  return (
    <div className="admin-panel">
      <header className="admin-panel__header">
        <div>
          <h2>Support Messages</h2>
          <p>View incoming requests with message content.</p>
        </div>
        <form className="admin-panel__search" onSubmit={handleSearch}>
          <select
            value={searchMode}
            onChange={(event) => setSearchMode(event.target.value)}
            disabled={isLoading}
          >
            <option value="email">Email</option>
            <option value="id">Request ID</option>
          </select>
          <input
            type="text"
            placeholder={searchMode === 'email' ? 'Search by email' : 'Search by request ID'}
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            disabled={isLoading}
          />
          <button type="submit" disabled={isLoading}>
            Search
          </button>
          {searchValue ? (
            <button type="button" onClick={clearSearch} disabled={isLoading}>
              Clear
            </button>
          ) : null}
        </form>
        <div className="admin-panel__filters">
          <label>
            Page Size
            <select
              value={limit}
              onChange={(event) => {
                setLimit(Number(event.target.value));
                setOffset(0);
                setSearchValue('');
              }}
              disabled={isLoading}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </label>
        </div>
      </header>

      {error ? <div className="admin-alert admin-alert--error">{error}</div> : null}

      {isLoading && records.length === 0 ? (
        <div className="admin-loading">Loading support messages…</div>
      ) : records.length === 0 ? (
        <div className="admin-empty">No support requests found for this filter.</div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table admin-table--wide">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Topic</th>
                <th>Status</th>
                <th>Received</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.id}>
                  <td>{record.email || '—'}</td>
                  <td>{record.name || '—'}</td>
                  <td>{record.topic || '—'}</td>
                  <td>{record.status || 'new'}</td>
                  <td>{record.created_at ? new Date(record.created_at).toLocaleString() : '—'}</td>
                  <td className="admin-table__message">{record.message || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <footer className="admin-pagination">
        <div className="admin-pagination__info">
          {searchValue
            ? 'Search results'
            : `${pagination.totalCount || records.length} total • showing ${records.length}`}
        </div>
        <div className="admin-pagination__actions">
          <button type="button" onClick={() => fetchRecords({ nextOffset: offset })} disabled={isLoading}>
            Refresh
          </button>
          <button
            type="button"
            onClick={loadNext}
            disabled={isLoading || pagination.nextOffset == null || Boolean(searchValue)}
          >
            Load More
          </button>
        </div>
      </footer>
    </div>
  );
}

function AdminHome({ token, onUnauthorized, onLogout }) {
  return (
    <div className="admin-shell">
      <aside className="admin-shell__sidebar">
        <div className="admin-shell__brand">
          <img src="/Lown_logo_final_light.svg" alt="Lown" />
          <h1>Lown Admin</h1>
        </div>
        <nav className="admin-shell__nav">
          <NavLink to="./support-records" className={({ isActive }) => (isActive ? 'is-active' : '')}>
            Support Messages
          </NavLink>
          <NavLink to="./notifications" className={({ isActive }) => (isActive ? 'is-active' : '')}>
            Notification Preferences
          </NavLink>
        </nav>
        <button type="button" className="admin-shell__logout" onClick={onLogout}>
          Sign out
        </button>
      </aside>
      <main className="admin-shell__content">
        <Routes>
          <Route path="support-records" element={<AdminSupportRecords token={token} onUnauthorized={onUnauthorized} />} />
          <Route path="notifications" element={<AdminPreferencesViewer token={token} onUnauthorized={onUnauthorized} />} />
          <Route index element={<Navigate to="support-records" replace />} />
          <Route path="*" element={<Navigate to="support-records" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default function AdminPortal() {
  const { token, setToken, clearToken } = useAdminToken();

  if (!token) {
    return <AdminLogin onAuthenticated={setToken} />;
  }

  return (
    <AdminHome
      token={token}
      onUnauthorized={clearToken}
      onLogout={clearToken}
    />
  );
}
